import React, { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { submitContact } from "../lib/api";

const programOptions = [
  "Diploma in Business Management",
  "Diploma in Information Technology (Coming Soon)",
  "Diploma in Digital Marketing (Coming Soon)",
  "Certificate in Accounting & Bookkeeping (Coming Soon)",
  "Diploma in Supply Chain & Logistics (Coming Soon)",
  "Diploma in Healthcare Administration (Coming Soon)",
  "General inquiry",
];

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    program_of_interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success("Thank you! Our admissions team will be in touch shortly.");
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", program_of_interest: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        kicker="Contact"
        title="Talk to a real person at Heritage."
        subtitle="Whether you're exploring your options or ready to apply, our admissions team is here to help — usually within one business day."
      />

      <section className="hccbt-container section pt-0">
        <div className="grid lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-5 space-y-6">
            <div className="card-soft p-6 flex items-start gap-4" data-testid="contact-info-phone">
              <div className="w-11 h-11 rounded-full bg-[#87CEEB]/25 flex items-center justify-center text-[#0F172A]">
                <Phone size={18} />
              </div>
              <div>
                <div className="overline">Phone</div>
                <a href="tel:+16043076440" className="font-display text-xl text-[#0F172A] mt-1 block hover:underline">
                  +1 (604) 307-6440
                </a>
                <p className="text-xs text-[#64748B] mt-1">Mon–Fri · 9:00 AM – 6:00 PM ET</p>
              </div>
            </div>

            <div className="card-soft p-6 flex items-start gap-4" data-testid="contact-info-email">
              <div className="w-11 h-11 rounded-full bg-[#87CEEB]/25 flex items-center justify-center text-[#0F172A]">
                <Mail size={18} />
              </div>
              <div>
                <div className="overline">Email</div>
                <a href="mailto:info@hccbt.com" className="font-display text-xl text-[#0F172A] mt-1 block hover:underline">
                  info@hccbt.com
                </a>
                <p className="text-xs text-[#64748B] mt-1">We reply within one business day.</p>
              </div>
            </div>

            <div className="card-soft p-6 flex items-start gap-4" data-testid="contact-info-address">
              <div className="w-11 h-11 rounded-full bg-[#87CEEB]/25 flex items-center justify-center text-[#0F172A]">
                <MapPin size={18} />
              </div>
              <div>
                <div className="overline">Campus</div>
                <div className="font-display text-xl text-[#0F172A] mt-1">Ontario, Canada</div>
                <p className="text-xs text-[#64748B] mt-1">Detailed address shared upon application or appointment.</p>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-7">
            <form onSubmit={onSubmit} className="card-soft p-8 md:p-10" data-testid="contact-form">
              <h2 className="font-display text-3xl text-[#0F172A] leading-tight">Send us a message</h2>
              <p className="text-sm text-[#475569] mt-2">We'll get back to you with next steps and program info.</p>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-semibold text-[#334155] tracking-wider uppercase">Full name</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    required
                    data-testid="contact-name-input"
                    className="mt-2 w-full bg-white border border-[#E2E8F0] rounded-lg px-4 py-3 text-sm text-[#0F172A]"
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#334155] tracking-wider uppercase">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    required
                    data-testid="contact-email-input"
                    className="mt-2 w-full bg-white border border-[#E2E8F0] rounded-lg px-4 py-3 text-sm text-[#0F172A]"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#334155] tracking-wider uppercase">Phone (optional)</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    data-testid="contact-phone-input"
                    className="mt-2 w-full bg-white border border-[#E2E8F0] rounded-lg px-4 py-3 text-sm text-[#0F172A]"
                    placeholder="+1 (___) ___-____"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#334155] tracking-wider uppercase">Program of interest</label>
                  <select
                    name="program_of_interest"
                    value={form.program_of_interest}
                    onChange={onChange}
                    data-testid="contact-program-select"
                    className="mt-2 w-full bg-white border border-[#E2E8F0] rounded-lg px-4 py-3 text-sm text-[#0F172A]"
                  >
                    <option value="">Choose a program</option>
                    {programOptions.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-5">
                <label className="text-xs font-semibold text-[#334155] tracking-wider uppercase">Message</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  required
                  rows={5}
                  data-testid="contact-message-input"
                  className="mt-2 w-full bg-white border border-[#E2E8F0] rounded-lg px-4 py-3 text-sm text-[#0F172A]"
                  placeholder="Tell us a little about your goals or questions…"
                />
              </div>

              <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
                <p className="text-xs text-[#64748B] max-w-md">
                  By submitting, you agree to be contacted by Heritage Career College about your inquiry.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary disabled:opacity-60"
                  data-testid="contact-submit-button"
                >
                  {submitting ? "Sending…" : (<>Send message <Send size={16} /></>)}
                </button>
              </div>

              {submitted && (
                <div className="mt-6 p-4 rounded-lg bg-[#87CEEB]/20 border border-[#87CEEB]/40 text-sm text-[#1e3a5f]" data-testid="contact-success-banner">
                  Thanks! Your inquiry was received. Our admissions team will reach out shortly.
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
